import { Controller } from "@hotwired/stimulus";

// A typeahead over the lead sources. The text input filters the list embedded in the page and
// the hidden input carries the chosen id, so forms submit exactly what the old <select> sent.
export default class extends Controller {
  static targets = ["value", "query", "list"];
  static values = { options: Array, limit: { type: Number, default: 25 } };

  connect () {
    this.matches = [];
    this.activeIndex = -1;
    this.notifiedValue = this.valueTarget.value;
  }

  open () {
    this.render();
  }

  search () {
    if (this.queryTarget.value.trim() === "") this.valueTarget.value = "";
    this.render();
  }

  blur () {
    this.settle();
  }

  // Leaving the field (or pressing Escape) reconciles the text with a choice: a full name
  // typed over an earlier choice counts as choosing it; anything else snaps back to the
  // chosen name, or clears. Clicking an option fires mousedown (pick) before blur, so a
  // click has already settled by the time we get here.
  settle () {
    const typed = this.queryTarget.value.trim().toLowerCase();
    const exact = typed === "" ? undefined : this.optionsValue.find(option => option.name.toLowerCase() === typed);

    if (exact) {
      this.choose(exact);
      return;
    }

    const selected = this.selectedOption();
    this.queryTarget.value = selected ? selected.name : "";
    this.close();
  }

  keydown (event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (this.closed()) this.render();
        this.moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActive(-1);
        break;
      case "Enter":
        if (this.closed()) return;
        event.preventDefault();
        if (this.matches[this.activeIndex]) this.choose(this.matches[this.activeIndex]);
        break;
      case "Escape":
        this.settle();
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  // A mousedown on the list's scrollbar would otherwise blur the input and close the list mid-drag.
  keepFocus (event) {
    event.preventDefault();
  }

  pick (event) {
    event.preventDefault();
    const option = this.optionsValue.find(candidate => String(candidate.id) === event.currentTarget.dataset.id);
    if (option) this.choose(option);
  }

  choose (option) {
    this.queryTarget.value = option.name;
    this.valueTarget.value = String(option.id);
    this.close();
  }

  close () {
    this.listTarget.classList.add("hidden");
    this.queryTarget.setAttribute("aria-expanded", "false");
    this.queryTarget.removeAttribute("aria-activedescendant");
    this.activeIndex = -1;
    this.notify();
  }

  // Listeners (the leads index submits its filter form on change) hear one change per
  // settled choice, not one per keystroke.
  notify () {
    if (this.valueTarget.value === this.notifiedValue) return;

    this.notifiedValue = this.valueTarget.value;
    this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  render () {
    const query = this.queryTarget.value.trim().toLowerCase();
    const all = this.optionsValue.filter(option => option.name.toLowerCase().includes(query));

    this.matches = all.slice(0, this.limitValue);
    this.activeIndex = -1;

    const items = this.matches.map((option, index) => this.item(option, index));
    if (all.length === 0) items.push(this.hint("No lead source matches"));
    if (all.length > this.matches.length) items.push(this.hint(`Showing ${this.matches.length} of ${all.length}. Keep typing to narrow the list.`));

    this.listTarget.replaceChildren(...items);
    this.listTarget.classList.remove("hidden");
    this.queryTarget.setAttribute("aria-expanded", "true");
  }

  item (option, index) {
    const li = document.createElement("li");
    li.id = `${this.listTarget.id}-${index}`;
    li.setAttribute("role", "option");
    li.setAttribute("aria-selected", "false");
    li.dataset.id = String(option.id);
    li.dataset.action = "mousedown->source-picker#pick";
    li.className = "cursor-pointer px-4 py-2 flex justify-between gap-4";
    li.append(this.text("span", option.name));
    if (option.inactive) li.append(this.text("span", "inactive", "ts-text-subdued"));
    return li;
  }

  hint (message) {
    const li = document.createElement("li");
    li.className = "px-4 py-2 ts-text-subdued";
    li.textContent = message;
    return li;
  }

  text (tag, content, className = "") {
    const element = document.createElement(tag);
    element.textContent = content;
    element.className = className;
    return element;
  }

  moveActive (step) {
    if (this.matches.length === 0) return;

    const next = (this.activeIndex + step + this.matches.length) % this.matches.length;
    const options = this.listTarget.querySelectorAll("[role=option]");

    options[this.activeIndex]?.setAttribute("aria-selected", "false");
    options[this.activeIndex]?.classList.remove("bg-gray-100");
    options[next].setAttribute("aria-selected", "true");
    options[next].classList.add("bg-gray-100");
    options[next].scrollIntoView({ block: "nearest" });
    this.queryTarget.setAttribute("aria-activedescendant", options[next].id);
    this.activeIndex = next;
  }

  closed () {
    return this.listTarget.classList.contains("hidden");
  }

  selectedOption () {
    const id = this.valueTarget.value;
    return id === "" ? undefined : this.optionsValue.find(option => String(option.id) === id);
  }
}
